import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { UtensilsCrossed, Pencil, Trash2, Send, Clock3, ThumbsUp, Save, Download, CopyPlus, MessageCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import RichTextEditor from "@/components/RichTextEditor";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEALS = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "supper", label: "Supper" },
];

const emptyMenu = () => ({
  Monday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Tuesday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Wednesday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Thursday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Friday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Saturday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  Sunday: { breakfast: "", lunch: "", dinner: "", supper: "" },
});

const emptyMealSchedule = () => ({
  weekday: { breakfast: "", lunch: "", dinner: "", supper: "" },
  weekend: { breakfast: "", lunch: "", dinner: "", supper: "" },
});

const formatDate = (iso) => {
  if (!iso) return "This week";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const timeAgo = (iso) => {
  if (!iso) return "just now";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function DewanMakan() {
  const { user } = useAuth();
  const [menu, setMenu] = useState({ week_start: "", days: emptyMenu(), comments: [] });
  const [commentText, setCommentText] = useState("");
  const [anonymousComment, setAnonymousComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replySaving, setReplySaving] = useState(false);
  const [mealSchedule, setMealSchedule] = useState(emptyMealSchedule());
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleUpdatedBy, setScheduleUpdatedBy] = useState("");

  const canManageMenu = useMemo(() => {
    if (!user) return false;
    return user.role === "admin" || ["Hostel Supervisor", "Dining Hall Supervisor"].includes(user.staff_role || "");
  }, [user]);

  const loadMenu = async (weekStart) => {
    try {
      const response = await api.get("/menu-week", weekStart ? { params: { week_start: weekStart } } : {});
      const data = response.data || { week_start: "", days: emptyMenu(), comments: [] };
      setMenu({
        week_start: data.week_start || "",
        days: data.days || emptyMenu(),
        comments: data.comments || [],
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load menu");
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.get("/menu-week/templates");
      const nextTemplates = response.data || [];
      setTemplates(nextTemplates);
      setSelectedTemplateId((currentId) => (
        nextTemplates.some((template) => template.id === currentId)
          ? currentId
          : nextTemplates[0]?.id || ""
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load menu templates");
    }
  };

  const loadMealSchedule = async () => {
    try {
      const response = await api.get("/meal-schedule");
      setMealSchedule(response.data?.schedule || emptyMealSchedule());
      setScheduleUpdatedBy(response.data?.updated_by || "");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to load meal schedule");
    }
  };

  useEffect(() => {
    loadMenu();
    loadTemplates();
    loadMealSchedule();
  }, []);

  const updateMenuField = (day, meal, value) => {
    setMenu((prev) => ({
      ...prev,
      days: {
        ...prev.days,
        [day]: { ...prev.days[day], [meal]: value },
      },
    }));
  };

  const saveMenu = async () => {
    if (!canManageMenu) {
      toast.error("Only admin, Hostel Supervisor and Dining Hall Supervisor can update the menu");
      return;
    }
    setSaving(true);
    try {
      await api.post("/menu-week", {
        week_start: menu.week_start || "",
        days: menu.days,
      });
      toast.success("Weekly menu saved");
      await loadMenu(menu.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save menu");
    } finally {
      setSaving(false);
    }
  };

  const updateMealSchedule = (period, meal, value) => {
    setMealSchedule((current) => ({
      ...current,
      [period]: { ...current[period], [meal]: value },
    }));
  };

  const saveMealSchedule = async () => {
    setSavingSchedule(true);
    try {
      const response = await api.put("/meal-schedule", mealSchedule);
      setMealSchedule(response.data.schedule);
      setScheduleUpdatedBy(response.data.updated_by || "");
      toast.success("Meal Schedule saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save Meal Schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const saveTemplate = async () => {
    const name = templateName.trim();
    if (!name) {
      toast.error("Enter a name for this menu template");
      return;
    }
    setSavingTemplate(true);
    try {
      const response = await api.post("/menu-week/templates", { name, days: menu.days });
      setTemplateName("");
      await loadTemplates();
      setSelectedTemplateId(response.data.id);
      toast.success(`Template “${name}” saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to save menu template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const applyTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      await api.post(`/menu-week/templates/${selectedTemplateId}/apply`, { week_start: menu.week_start });
      await loadMenu(menu.week_start);
      toast.success("Template applied to the selected week");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to apply menu template");
    }
  };

  const downloadTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      const response = await api.get(`/menu-week/templates/${selectedTemplateId}/download`, { responseType: "blob" });
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);
      link.href = objectUrl;
      link.download = `${(selectedTemplate?.name || "menu-template").replace(/[^\w.-]+/g, "-")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to download menu template");
    }
  };

  const addComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    try {
      await api.post("/menu-week/comments", { text, anonymous: anonymousComment, week_start: menu.week_start });
      setCommentText("");
      setAnonymousComment(false);
      await loadMenu(menu.week_start);
      toast.success("Comment posted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to add comment");
    }
  };

  const addReply = async (commentId) => {
    const text = (replyDrafts[commentId] || "").trim();
    if (!text) return;
    setReplySaving(true);
    try {
      await api.post(`/menu-week/comments/${commentId}/replies`, { text, week_start: menu.week_start });
      setReplyDrafts((current) => ({ ...current, [commentId]: "" }));
      setReplyingToCommentId(null);
      await loadMenu(menu.week_start);
      toast.success("Reply posted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to post reply");
    } finally {
      setReplySaving(false);
    }
  };

  const toggleCommentLike = async (commentId) => {
    try {
      await api.post(`/menu-week/comments/${commentId}/like`, null, { params: { week_start: menu.week_start } });
      await loadMenu(menu.week_start);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to react to comment");
    }
  };

  const updateComment = async (commentId) => {
    const text = editingCommentText.trim();
    if (!text) return;
    try {
      await api.put(`/menu-week/comments/${commentId}`, { text, week_start: menu.week_start });
      setEditingCommentId(null);
      setEditingCommentText("");
      await loadMenu(menu.week_start);
      toast.success("Comment updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update comment");
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/menu-week/comments/${commentId}`, { params: { week_start: menu.week_start } });
      await loadMenu(menu.week_start);
      toast.success("Comment deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to delete comment");
    }
  };

  return (
    <div className="space-y-8" data-testid="dewan-makan-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Refectory &amp; Meals</p>
          <h1 className="font-display text-3xl font-extrabold mt-1">Weekly Menu</h1>
        </div>
        <Badge variant="secondary" className="self-start flex items-center gap-2 text-sm">
          <UtensilsCrossed className="h-4 w-4" />
          {formatDate(menu.week_start)}
        </Badge>
      </div>

      <section className="rounded-2xl border border-border border-t-4 border-sky-500 bg-card p-4 shadow-sm sm:p-6" data-testid="meal-schedule-section">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold">Meal Schedule</h2>
            {scheduleUpdatedBy && <p className="mt-1 text-xs text-muted-foreground">Updated by {scheduleUpdatedBy}</p>}
          </div>
          {canManageMenu && (
            <Button onClick={saveMealSchedule} disabled={savingSchedule} data-testid="save-meal-schedule-button">
              {savingSchedule ? "Saving…" : "Save Meal Schedule"}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full border-collapse text-left">
            <thead>
              <tr>
                <th className="w-36 border-b border-border px-3 py-2 text-sm font-semibold">Meal</th>
                <th className="border-b border-border px-3 py-2 text-sm font-semibold">Weekdays</th>
                <th className="border-b border-border px-3 py-2 text-sm font-semibold">Weekend</th>
              </tr>
            </thead>
            <tbody>
              {MEALS.map((meal) => (
                <tr key={meal.key} className="align-top">
                  <th scope="row" className="border-b border-border px-3 py-3 text-sm font-medium">{meal.label}</th>
                  {["weekday", "weekend"].map((period) => (
                    <td key={`${period}-${meal.key}`} className="w-[38%] border-b border-border px-3 py-3">
                      {canManageMenu ? (
                        <RichTextEditor
                          value={mealSchedule[period]?.[meal.key] || ""}
                          onChange={(value) => updateMealSchedule(period, meal.key, value)}
                          ariaLabel={`${period} ${meal.label} time`}
                        />
                      ) : (
                        <div className="min-h-10 rounded-md border border-dashed border-border bg-secondary/30 px-3 py-2">
                          <RichTextEditor
                            readOnly
                            value={mealSchedule[period]?.[meal.key] || "No time set"}
                            ariaLabel={`${period} ${meal.label} time`}
                          />
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="rounded-2xl border border-border border-t-4 border-emerald-500 bg-card p-4 sm:p-6 shadow-sm">
        <div className="mb-4 flex flex-col gap-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-display text-xl font-bold">Menu of the week</h2>
            <div className="flex flex-wrap items-end gap-2">
              <label className="grid gap-1 text-xs font-medium text-muted-foreground">
                Week beginning
                <Input
                  type="date"
                  value={menu.week_start}
                  onChange={(event) => loadMenu(event.target.value)}
                  className="w-auto"
                />
              </label>
              {canManageMenu && (
                <Button onClick={saveMenu} disabled={saving} data-testid="save-week-menu-button">
                  {saving ? "Saving…" : "Save menu"}
                </Button>
              )}
            </div>
          </div>
          {canManageMenu && (
            <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder="Name this menu template"
                aria-label="Menu template name"
                className="sm:max-w-[220px]"
              />
              <Button variant="outline" onClick={saveTemplate} disabled={savingTemplate}>
                <Save className="h-4 w-4" /> {savingTemplate ? "Saving…" : "Save template"}
              </Button>
              <select
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                aria-label="Saved menu templates"
                className="h-9 min-w-[180px] rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Choose a saved template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
              <Button variant="secondary" onClick={applyTemplate} disabled={!selectedTemplateId}>
                <CopyPlus className="h-4 w-4" /> Apply to week
              </Button>
              <Button variant="ghost" onClick={downloadTemplate} disabled={!selectedTemplateId} aria-label="Download menu template">
                <Download className="h-4 w-4" /> Download CSV
              </Button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left">
            <thead>
              <tr>
                <th className="border-b border-border px-3 py-2 text-sm font-semibold">Day</th>
                {MEALS.map((meal) => (
                  <th key={meal.key} className="border-b border-border px-3 py-2 text-sm font-semibold">
                    {meal.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map((day) => (
                <tr key={day} className="align-top">
                  <td className="border-b border-border px-3 py-3 text-sm font-medium text-muted-foreground">{day}</td>
                  {MEALS.map((meal) => (
                    <td key={`${day}-${meal.key}`} className="border-b border-border px-3 py-3">
                      {canManageMenu ? (
                        <Textarea
                          value={menu.days?.[day]?.[meal.key] || ""}
                          onChange={(e) => updateMenuField(day, meal.key, e.target.value)}
                          rows={3}
                          placeholder={`${meal.label} menu`}
                          className="min-w-[180px] resize-none"
                        />
                      ) : (
                        <div className="min-h-[72px] rounded-md border border-dashed border-border bg-secondary/40 px-2 py-2 text-sm text-muted-foreground whitespace-pre-wrap">
                          {menu.days?.[day]?.[meal.key] || "No menu set"}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold mb-4">Comments & suggestions</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Share a comment, idea, suggestion or food report…"
            className="flex-1"
          />
          <Button onClick={addComment} className="sm:w-auto">
            <Send className="h-4 w-4 mr-2" /> Post
          </Button>
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox checked={anonymousComment} onCheckedChange={setAnonymousComment} />
          Post anonymously
        </label>

        <div className="mt-5 space-y-3">
          {(!menu.comments || menu.comments.length === 0) && (
            <div className="rounded-xl border border-dashed border-border p-5 text-sm text-muted-foreground">
              No comments yet. Let everyone know your thoughts about the menu.
            </div>
          )}

          {(menu.comments || []).map((comment) => {
            const isOwner = comment.is_owner;
            const isEditing = editingCommentId === comment.id;

            return (
              <div key={comment.id} className="rounded-xl border border-border bg-secondary/30 p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{comment.author_name}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock3 className="h-3 w-3" /> {timeAgo(comment.created_at)}
                      </span>
                    </div>
                  </div>
                  {(isOwner || user?.role === "admin") && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => {
                        setEditingCommentId(comment.id);
                        setEditingCommentText(comment.text || "");
                      }} aria-label="Edit comment">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteComment(comment.id)} aria-label="Delete comment">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      value={editingCommentText}
                      onChange={(e) => setEditingCommentText(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => updateComment(comment.id)}>Save</Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditingCommentId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 text-sm whitespace-pre-wrap text-foreground">{comment.text}</p>
                )}
                <div className="mt-3">
                  <Button
                    variant={comment.is_liked ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => toggleCommentLike(comment.id)}
                    aria-pressed={comment.is_liked}
                    aria-label={comment.is_liked ? "Unlike comment" : "Like comment"}
                  >
                    <ThumbsUp className="h-4 w-4" /> {comment.likes_count || 0}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReplyingToCommentId((current) => current === comment.id ? null : comment.id)}
                    aria-expanded={replyingToCommentId === comment.id}
                    aria-label={`Reply to ${comment.author_name}'s comment`}
                  >
                    <MessageCircle className="h-4 w-4" /> Reply{comment.replies?.length ? ` · ${comment.replies.length}` : ""}
                  </Button>
                </div>
                {comment.replies?.length > 0 && (
                  <div className="ml-2 mt-3 space-y-3 border-l-2 border-border pl-4">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="space-y-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="text-xs font-semibold">{reply.author_name}</span>
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Clock3 className="h-3 w-3" /> {timeAgo(reply.created_at)}
                          </span>
                        </div>
                        <p className="whitespace-pre-wrap text-sm text-foreground">{reply.text}</p>
                      </div>
                    ))}
                  </div>
                )}
                {replyingToCommentId === comment.id && (
                  <form className="mt-3 space-y-2" onSubmit={(event) => { event.preventDefault(); addReply(comment.id); }}>
                    <Textarea
                      value={replyDrafts[comment.id] || ""}
                      onChange={(event) => setReplyDrafts((current) => ({ ...current, [comment.id]: event.target.value }))}
                      rows={2}
                      placeholder="Write a reply…"
                      aria-label={`Reply to ${comment.author_name}'s comment`}
                    />
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={replySaving || !(replyDrafts[comment.id] || "").trim()}>
                        <Send className="h-4 w-4" /> {replySaving ? "Posting…" : "Post reply"}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setReplyingToCommentId(null)}>Cancel</Button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
